import { Client } from '@datocms/cma-client-browser';
import { GraphQLClient } from 'graphql-request';
import { TranscriptData, TranscriptElement, TranscriptVoiceMapping } from '../types.ts/transcript';
import ApiClient from '../apiClient/ApiClient';
import { Subtitles } from '../types.ts/story';

/**
 * Enum for transcript versions
 */
export enum TranscriptVersion {
  LATEST = 'latest',
  ORIGINAL = 'original',
  REV = 'rev',
  AWS = 'aws'
}

const TRANSCRIPT_UPLOAD_QUERY = `query getTranscript($id: UploadId) {
    allUploads(filter: {id: {eq: $id}}) {
      id
      url
    }
  }`;

const UPLOAD_BY_TITLE_QUERY = `query getUpload($title: String!) {
    allUploads(filter: {title: {eq: $title}}) {
      id
      url
      customData
    }
  }`;

const AUDIO_UPLOAD_BY_FILENAME_QUERY = `query getUpload($filename: String!) {
  allUploads(filter: {filename: {matches: {pattern: $filename}}, AND: {type: {eq: audio}}}) {
    id
    url
    customData
  }
}`;

const STORY_TRANSCRIPT_CHANGES_QUERY = `query getStory($id: ItemId) {
    story(filter: {id: {eq: $id}}) {
      transcriptionChanges
    }
  }`;

const ORIGINAL_VIDEO_TRANSCRIPTION_QUERY = `query getStory($id: ItemId) {
  story(filter: {id: {eq: $id}}) {
    id
    transcription {
      jobStatus
      elementsJson {
        id
        url
      }
    }
  }
}`;

export class TranscriptRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  
  /**
   * Get transcript by story ID
   * 
   * @param storyId Story ID to fetch transcript for
   * @returns TranscriptContainer or null if not found
   */
  async getTranscriptByStoryId(storyId: string): Promise<any> {
    try {
      // In a real implementation, this would fetch from DatoCMS or another API
      // For now this is a stub implementation
      console.log(`Fetching transcript for story: ${storyId}`);
      return null;
    } catch (error) {
      console.error('Error fetching transcript:', error);
      return null;
    }
  }
  
  /**
   * Get audio and waveform URLs for a story
   * 
   * @param storyId Story ID to fetch URLs for
   * @returns Object with audioUrl and waveformUrl
   */
  async getAudioAndWaveformUrlsByStoryId(storyId: string): Promise<{audioUrl: string, waveformUrl?: string}> {
    try {
      // In a real implementation, this would fetch from DatoCMS or another API
      // For now this is a stub implementation
      console.log(`Fetching audio URLs for story: ${storyId}`);
      return {
        audioUrl: '',
        waveformUrl: ''
      };
    } catch (error) {
      console.error('Error fetching audio URLs:', error);
      return {
        audioUrl: '',
        waveformUrl: ''
      };
    }
  }

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  async getTranscriptionFromDato(storyId: string) {
    const queryResult: {
      story: {
        transcription: {
          jobStatus: string;
          elementsJson: {
            id: string;
            url: string;
          };
        };
        id: string;
      };
    } = await this.gqlClient.request(ORIGINAL_VIDEO_TRANSCRIPTION_QUERY, {
      id: storyId,
    });
    return queryResult.story.transcription;
  }

  /**
   * Fetches a transcript from a specific URL
   * 
   * @param transcriptUrl URL to fetch transcript from
   * @returns Transcript data
   */
  async fetchTranscriptFromUrl(transcriptUrl: string): Promise<TranscriptData> {
    const jsonResponse = await fetch(transcriptUrl);
    const transcriptData = (await jsonResponse.json()) as TranscriptData;
    if (!transcriptData.language) {
      transcriptData.language = 'en';
    }
    return transcriptData;
  }

  /**
   * Fetches a transcript by version
   * 
   * @param transcriptUrls Object containing URLs for different transcript versions
   * @param version The version to fetch
   * @returns The transcript data for the specified version
   */
  async fetchTranscriptByVersion(
    transcriptUrls: Record<TranscriptVersion, string>,
    version: TranscriptVersion
  ): Promise<TranscriptData | null> {
    try {
      const url = transcriptUrls[version];
      if (!url) {
        console.error(`URL for transcription version ${version} not found`);
        return null;
      }
      return await this.fetchTranscriptFromUrl(url);
    } catch (error) {
      console.error(`Error fetching transcript version ${version}:`, error);
      return null;
    }
  }

  async generateSubtitles(
    transcriptionElements: TranscriptElement[],
    language: string,
  ): Promise<Subtitles | null> {
    const fetchUrl = `${process.env.REACT_APP_API_URL}/api/captions/get-translated`;
    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transcriptionElements,
        language,
      }),
    });

    if (!response.ok) {
      console.error('Failed to fetch subtitles', response.json());
      return null;
    }

    const subtitlesLines = (await response.json()).translatedCaptions;
    return { lines: subtitlesLines } as Subtitles;
  }

  async fetchWaveformDataFromUrl(waveformUrl: string) {
    const jsonResponse = await fetch(waveformUrl);
    return jsonResponse.json();
  }

  async fetchWaveformData(uploadId: string) {
    const response: any = await this.gqlClient.request(
      TRANSCRIPT_UPLOAD_QUERY,
      {
        id: uploadId,
      },
    );

    const waveformFile = response.allUploads[0];
    if (!waveformFile) {
      throw Error(`Waveform file not found`);
    }

    const jsonResponse = await fetch(waveformFile.url);
    return jsonResponse.json();
  }

  async fetchWaveformDataForAudioByUrl(url: string) {
    // https://www.datocms-assets.com/99106/1731701509-hello-morning-122692.mp3 => hello-morning-122692.mp3
    const filename = url.split('/').pop()!.split('-').slice(1).join('-');
    const response: any = await this.gqlClient.request(
      AUDIO_UPLOAD_BY_FILENAME_QUERY,
      {
        filename,
      },
    );

    const audio = response.allUploads[0];
    const waveformFileId = audio?.customData?.waveformDataUploadId;
    const duration = parseFloat(audio?.customData?.duration) || 0;

    if (!waveformFileId) {
      throw Error(`Waveform file Id not found`);
    }

    return {
      waveformJson: await this.fetchWaveformData(waveformFileId),
      duration,
    };
  }

  /**
   * Fetches speaker mappings from a URL
   * 
   * @param url URL to fetch speaker mappings from
   * @returns Speaker mappings
   */
  async fetchSpeakerMappings(url: string): Promise<TranscriptVoiceMapping> {
    const response = await fetch(url);
    return await response.json() as TranscriptVoiceMapping;
  }
}
